import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { useCalendarNavigation } from '../context/CalendarNavigationContext';
import {
  getEventsForDay,
  sortEventsByTime,
  groupEventsByDay,
  detectOverlappingEvents,
  calculateEventPosition,
} from '../utils/eventPositioning';
import { getWeekDays } from '../utils/dateHelpers';
import { CALENDAR_CONSTANTS } from '../utils/constants';

export const useCalendarEvents = ({
  events = [],
  startHour = CALENDAR_CONSTANTS.DEFAULT_START_HOUR,
  hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT,
}) => {
  const { currentWeekStart } = useCalendarNavigation();

  const weekDays = useMemo(() => {
    return getWeekDays(currentWeekStart);
  }, [currentWeekStart]);

  const eventsByDay = useMemo(() => {
    return groupEventsByDay(events);
  }, [events]);

  const getEventsForDayWithPositions = useCallback((dayDate) => {
    const dayEvents = getEventsForDay(events, dayDate);
    const sortedEvents = sortEventsByTime(dayEvents);
    const overlaps = detectOverlappingEvents(sortedEvents);

    return sortedEvents.map((event) => {
      const position = calculateEventPosition(event, startHour, hourHeight);
      const overlapInfo = overlaps.get(event.id || `${event.startTime}-${event.endTime}`) || {
        columnIndex: 0,
        totalColumns: 1,
        width: '100%',
        left: '0',
      };

      return {
        ...event,
        position,
        overlap: overlapInfo,
      };
    });
  }, [events, startHour, hourHeight]);

  const getDayEvents = useCallback((dayKey) => {
    return eventsByDay[dayKey] || [];
  }, [eventsByDay]);

  const hasEventsOnDay = useCallback((dayKey) => {
    return (eventsByDay[dayKey] || []).length > 0;
  }, [eventsByDay]);

  const getEventCountForDay = useCallback((dayKey) => {
    return (eventsByDay[dayKey] || []).length;
  }, [eventsByDay]);

  const findEventById = useCallback((eventId) => {
    return events.find((event) => event.id === eventId);
  }, [events]);

  const getEventsInTimeRange = useCallback((startTime, endTime) => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);

    return events.filter((event) => {
      const eventStart = dayjs(event.startTime);
      const eventEnd = dayjs(event.endTime);

      return (
        (eventStart.isBefore(end) && eventEnd.isAfter(start)) ||
        eventStart.isSame(start) ||
        eventEnd.isSame(end)
      );
    });
  }, [events]);

  const totalEventsCount = events.length;

  return {
    weekDays,
    eventsByDay,
    totalEventsCount,

    getEventsForDayWithPositions,
    getDayEvents,
    hasEventsOnDay,
    getEventCountForDay,
    findEventById,
    getEventsInTimeRange,
  };
};

export default useCalendarEvents;
